import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { CONSENT_CHANGE_EVENT, readConsent } from './consent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

export const AdContext = createContext({
  consent: { ads: 'unknown', analytics: 'unknown', updatedAt: null },
  route: '/',
  placements: [],
  isPremium: false,
  refresh: () => {},
});

const decodeJwtPayload = (token) => {
  try {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const getIsPremium = () => {
  const adminUserRaw = localStorage.getItem('user');
  if (adminUserRaw) {
    try {
      const u = JSON.parse(adminUserRaw);
      if (u?.role === 'admin') return true;
    } catch {
      // ignore
    }
  }

  const token = localStorage.getItem('userToken') || localStorage.getItem('token');
  const payload = decodeJwtPayload(token);
  return ['premium_user', 'admin'].includes(payload?.role);
};

function AdProvider({ children }) {
  const location = useLocation();
  const route = location.pathname || '/';
  const [placements, setPlacements] = useState([]);
  const [consent, setConsent] = useState(() => readConsent());
  const [isPremium, setIsPremium] = useState(() => getIsPremium());

  const refresh = useCallback(async () => {
    try {
      const token = localStorage.getItem('userToken') || localStorage.getItem('token');
      const res = await axios.get(`${API}/ads/placements`, {
        params: { route },
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setPlacements(res.data || []);
    } catch {
      setPlacements([]);
    }
  }, [route]);

  useEffect(() => {
    setConsent(readConsent());
    setIsPremium(getIsPremium());
    refresh();
  }, [refresh]);

  useEffect(() => {
    const onConsentChange = (event) => {
      setConsent(event?.detail || readConsent());
    };
    const onStorage = (e) => {
      if (e.key === 'tp_consent_v1') {
        setConsent(readConsent());
      }
      if (e.key === 'userToken' || e.key === 'token' || e.key === 'user') {
        setIsPremium(getIsPremium());
        refresh();
      }
    };
    window.addEventListener(CONSENT_CHANGE_EVENT, onConsentChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, onConsentChange);
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({ consent, route, placements, isPremium, refresh }),
    [consent, route, placements, isPremium, refresh]
  );

  return <AdContext.Provider value={value}>{children}</AdContext.Provider>;
}

export default AdProvider;
